import {
    useCallback,
    useEffect,
    useReducer,
    useRef,
    useMemo,
    useLayoutEffect,
} from 'react';
import { omit } from 'lodash';
import {
    useSearchContext,
    type SearchContextValue,
} from '@splunk/dashboard-context';
import type {
    SearchData,
    RequestParams,
    RefreshFunction,
    DataSourceBindingMap,
    BindingType,
    SearchControlFunction,
} from '@splunk/dashboard-types';
import usePrevious from './usePrevious';
import useEventCallback from './useEventCallback';

type UnsubscribeCallback = ReturnType<SearchContextValue['subscribe']>;

type Action =
    | { type: 'binding/remove'; bindingType: string }
    | { type: 'binding/reset'; bindingType: string }
    | {
          type: 'binding/update';
          bindingType: BindingType;
          payload: SearchData;
      };

export type DataSources = Record<BindingType, SearchData>;

export interface UseSubscribeToSearchesReturnType {
    dataSources: DataSources;
    loading: boolean;
    updateRequestParams: (
        bindingType: BindingType,
        newRequestParams: RequestParams
    ) => void;
    refresh: RefreshFunction;
    control: SearchControlFunction;
}

type UseSubscribeToSearchesType = ({
    consumerId,
    bindings,
    subscribeFn,
    refreshKey,
}: {
    consumerId: string;
    bindings: DataSourceBindingMap;
    subscribeFn?: SearchContextValue['subscribe'];
    refreshKey?: string;
}) => UseSubscribeToSearchesReturnType;

const EMPTY_STATE = {};

const getUnneededConsumers = ({
    previousBindings,
    newBindings,
    isNewConsumerId,
}: {
    previousBindings: DataSourceBindingMap;
    newBindings: DataSourceBindingMap;
    isNewConsumerId: boolean;
}) => {
    const previousBindingTypes = Object.keys(previousBindings);
    if (isNewConsumerId) {
        // if the consumer id changes, all subscriptions must be recreated
        return previousBindingTypes;
    }

    const unneededConsumers: string[] = [];
    previousBindingTypes.forEach((prevBindingType) => {
        const prevDsId = previousBindings[prevBindingType];
        const newDsId = newBindings[prevBindingType];
        // if either the bindingType does not exist OR if it exists the and the dsId changed
        if (newDsId === undefined || prevDsId !== newDsId) {
            unneededConsumers.push(prevBindingType);
        }
    });

    return unneededConsumers;
};

export const reducer = (state: DataSources, action: Action): DataSources => {
    switch (action.type) {
        case 'binding/remove': {
            const { bindingType } = action;

            return omit(state, [bindingType]);
        }
        case 'binding/reset': {
            const { bindingType } = action;

            return {
                ...state,
                [bindingType]: {
                    error: undefined,
                    data: undefined,
                    meta: undefined,
                    requestParams: undefined,
                },
            };
        }
        case 'binding/update': {
            const { bindingType, payload } = action;

            return {
                ...state,
                [bindingType]: payload,
            };
        }
        default:
            return state ?? EMPTY_STATE;
    }
};

export const useSubscribeToSearches: UseSubscribeToSearchesType = ({
    consumerId,
    bindings,
    subscribeFn,
    refreshKey,
}) => {
    const [results, dispatch] = useReducer(reducer, {});
    const prevProps = usePrevious({ consumerId, bindings });
    const searchApi = useSearchContext();
    const subscribe = subscribeFn ?? searchApi.subscribe;
    const prevRefreshKey = usePrevious(refreshKey);
    const shouldReset =
        refreshKey && prevRefreshKey && refreshKey !== prevRefreshKey;

    const unsubscribeCallbacksRef = useRef<Record<string, UnsubscribeCallback>>(
        {}
    );

    useLayoutEffect(() => {
        if (shouldReset) {
            // on refreshKey change, reset all bindings to empty state
            Object.entries(bindings).forEach(([bindingType, dsId]) => {
                dispatch({
                    type: 'binding/reset',
                    bindingType,
                });
                // cycling the subscription, this will trigger a refresh if the requestParams have changed
                unsubscribeCallbacksRef.current[bindingType]?.(); // unsubscribe first
                unsubscribeCallbacksRef.current[bindingType] = subscribe({
                    dsId,
                    consumerId,
                    bindingType,
                    subscriberId: consumerId,
                    onUpdate: (payload: SearchData) => {
                        dispatch({
                            type: 'binding/update',
                            bindingType,
                            payload,
                        });
                    },
                });
            });
        }
    }, [shouldReset, bindings, consumerId, searchApi, subscribe]);

    useEffect(() => {
        const isNewConsumerId = prevProps?.consumerId !== consumerId;
        // Prune out consumers that are no longer in the binding list
        //   if the consumerId changed, all the subscriptions have to be cancelled
        const unneededConsumers = getUnneededConsumers({
            previousBindings: prevProps?.bindings ?? {},
            newBindings: bindings,
            isNewConsumerId,
        });

        unneededConsumers.forEach((bindingType) => {
            unsubscribeCallbacksRef.current[bindingType]?.();
            delete unsubscribeCallbacksRef.current[bindingType];
            dispatch({ type: 'binding/remove', bindingType });
        });

        Object.entries(bindings).forEach(([bindingType, dsId]) => {
            if (unsubscribeCallbacksRef.current[bindingType] !== undefined) {
                // if the subscription already exists, don't create it again
                return;
            }

            /**
             * Initialize the bound resource with dummy data so that things like progress bars will appear
             * even if the DS hasn't started passing data back
             */
            dispatch({
                type: 'binding/reset',
                bindingType,
            });

            const unsubscribe = subscribe({
                dsId,
                consumerId,
                bindingType,
                subscriberId: consumerId,
                onUpdate: (payload: SearchData) => {
                    dispatch({
                        type: 'binding/update',
                        bindingType,
                        payload,
                    });
                },
            });

            // store the callbacks for later use
            unsubscribeCallbacksRef.current[bindingType] = unsubscribe;
        });
    }, [
        bindings,
        consumerId,
        prevProps?.bindings,
        prevProps?.consumerId,
        subscribe,
    ]);

    useEffect(() => {
        // clean-up any subscriptions on unmount.
        return () => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            Object.values(unsubscribeCallbacksRef.current).forEach(
                (unsubscribe) => unsubscribe?.()
            );
        };
    }, []);

    const updateRequestParams = useCallback(
        (bindingType: string, newRequestParams: RequestParams) => {
            searchApi.updateRequestParams({
                consumerId,
                dsId: bindings[bindingType],
                bindingType,
                requestParams: newRequestParams,
            });
        },
        [bindings, consumerId, searchApi]
    );

    const refresh = useCallback<RefreshFunction>(
        (options) => {
            Object.entries(bindings).forEach(([bindingType, dsId]) => {
                dispatch({
                    type: 'binding/reset',
                    bindingType,
                });
                searchApi.refresh({ consumerId, bindingType, dsId, options });
            });
            return Promise.resolve();
        },
        [bindings, consumerId, searchApi]
    );

    const control = useCallback<SearchControlFunction>(
        async (action) =>
            Object.entries(bindings).reduce(
                async (previousControlAction, [bindingType, dsId]) => {
                    await previousControlAction;
                    return searchApi.control({
                        consumerId,
                        bindingType,
                        dsId,
                        action,
                    });
                },
                Promise.resolve()
            ),
        [bindings, consumerId, searchApi]
    );

    // TODO: let's revisit this logic. What does loading really mean? What if there's data but the search status is 'running'?
    // results.primary initially does not exist, but if there is a valid dataSourceId we can assume it is loading
    const loading = useMemo(() => {
        const primaryResults = shouldReset ? null : results.primary;
        if (Object.keys(bindings).length) {
            return primaryResults
                ? !primaryResults.error && !primaryResults.data
                : true;
        }
        return false;
    }, [shouldReset, results.primary, bindings]);

    return {
        loading,
        dataSources: shouldReset ? EMPTY_STATE : results,
        // by using useEventCallback, we avoid triggering multiple re-renders when these callbacks change
        updateRequestParams: useEventCallback(updateRequestParams),
        refresh: useEventCallback(refresh),
        control: useEventCallback(control),
    };
};
